<template>
  <section
    id="faq"
    class="faq-section"
  >
    <div class="section-container">
      <!-- Section Header -->
      <div class="section-header">
        <span class="section-badge">Need Help?</span>
        <h2 class="section-title">
          Frequently Asked Questions
        </h2>
        <p class="section-subtitle">
          Find quick answers to common questions about our services
        </p>
      </div>

      <!-- FAQ Grid -->
      <div class="faq-content">
        <!-- FAQ Accordion -->
        <div class="faq-list">
          <div
            v-for="(faq, index) in faqItems"
            :key="index"
            class="faq-item"
            :class="{ 'active': openIndex === index }"
          >
            <button
              class="faq-question"
              @click="toggleFaq(index)"
            >
              <span>{{ faq.question }}</span>
              <i
                :class="[
                  'fas',
                  openIndex === index ? 'fa-chevron-up' : 'fa-chevron-down'
                ]"
              />
            </button>
            <transition name="faq-expand">
              <div
                v-if="openIndex === index"
                class="faq-answer"
              >
                <div class="answer-content">
                  <p>{{ faq.answer }}</p>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <!-- Policies Card -->
        <div
          id="policies"
          class="policies-card"
        >
          <div class="policies-header">
            <i class="fas fa-file-contract" />
            <h3>Policies & Forms</h3>
          </div>
          <p class="policies-desc">
            Access important policies, guidelines, and downloadable forms.
          </p>
          <ul class="policies-list">
            <li>
              <router-link to="/policies">
                <i class="fas fa-file-pdf" />
                <span>Student Handbook</span>
              </router-link>
            </li>
            <li>
              <router-link to="/policies">
                <i class="fas fa-file-pdf" />
                <span>Code of Conduct</span>
              </router-link>
            </li>
            <li>
              <router-link to="/policies">
                <i class="fas fa-file-pdf" />
                <span>Activity Request Form</span>
              </router-link>
            </li>
            <li>
              <router-link to="/policies">
                <i class="fas fa-file-pdf" />
                <span>Org Registration Form</span>
              </router-link>
            </li>
            <li>
              <router-link to="/policies">
                <i class="fas fa-file-pdf" />
                <span>Clearance Form</span>
              </router-link>
            </li>
          </ul>
          <router-link
            to="/policies"
            class="view-all-link"
          >
            View All Documents
            <i class="fas fa-arrow-right" />
          </router-link>
        </div>
      </div>

      <!-- Still Have Questions Banner -->
      <div class="questions-banner">
        <div class="banner-content">
          <i class="fas fa-question-circle" />
          <div>
            <h4>Still have questions?</h4>
            <p>Our team is here to help you with any concerns.</p>
          </div>
        </div>
        <router-link
          to="/home/contact"
          class="banner-link"
        >
          Contact Us
          <i class="fas fa-arrow-right" />
        </router-link>
      </div>
    </div>
  </section>
</template>

<script>
import '../../../assets/css/landing/sections/faq.css'

export default {
  name: 'FaqSection',

  props: {
    faqs: {
      type: Array,
      default: () => []
    },
    faqOpen: {
      type: Number,
      default: null
    }
  },

  data () {
    return {
      openIndex: null,
      defaultFaqs: [
        {
          question: 'How do I schedule a counseling appointment?',
          answer: 'You can schedule a counseling appointment by visiting our Guidance & Counseling page and clicking the "Schedule Appointment" button. Alternatively, you can visit the OSAS office during office hours (Monday-Friday, 8AM-5PM), or call our hotline at (054) 123-4567. Walk-in consultations are also available but may require waiting depending on counselor availability.'
        },
        {
          question: 'What documents do I need for job placement assistance?',
          answer: 'For job placement assistance, you will need to prepare the following documents: updated resume/CV, official transcript of records, a completed job placement registration form (available at the OSAS office or downloadable from our Policies & Forms section), and a 2x2 ID photo. Additional documents such as certificates, awards, or recommendation letters may also strengthen your application for specific job opportunities.'
        },
        {
          question: 'How can I join a student organization?',
          answer: 'To join a student organization, first visit the Student Development office to get a complete list of recognized student organizations. Each organization has its own membership requirements, so you may need to attend orientation meetings or submit application forms. The best time to join is during the annual Student Organizations Fair held at the start of each semester where you can meet current members and learn about different groups.'
        },
        {
          question: 'Where can I report a lost or found item?',
          answer: 'Lost items can be reported at the OSAS Lost & Found section of our website or directly at the OSAS office. When reporting, please provide a detailed description of the item including brand, color, distinguishing marks, and the approximate location and time it was lost. Found items are kept at the office for 30 days. If unclaimed, items may be donated to charity.'
        },
        {
          question: 'How do I request a Good Moral Certificate?',
          answer: 'To request a Good Moral Certificate, submit a written request at the OSAS office or download the request form from our Policies & Forms section. Processing typically takes 3-5 working days. Important: Make sure you have no pending violations, disciplinary cases, or clearance issues as these may delay or prevent the issuance of your certificate. A valid school ID is required when claiming.'
        },
        {
          question: 'What are the office hours of OSAS?',
          answer: 'The Office of Student Affairs and Services is open Monday through Friday from 8:00 AM to 5:00 PM, with no noon break. On Saturdays, we are open from 8:00 AM to 12:00 PM by appointment only. We are closed on Sundays and holidays. For urgent matters outside office hours, you may contact our emergency hotline or send an email to osas@ncf.edu.ph.'
        }
      ]
    }
  },

  computed: {
    faqItems () {
      return this.faqs && this.faqs.length > 0 ? this.faqs : this.defaultFaqs
    }
  },

  watch: {
    faqOpen: {
      immediate: true,
      handler (val) {
        this.openIndex = val
      }
    }
  },

  methods: {
    toggleFaq (index) {
      if (this.openIndex === index) {
        this.openIndex = null
        this.$emit('toggle-faq', null)
      } else {
        this.openIndex = index
        this.$emit('toggle-faq', index)
      }
    }
  }
}
</script>

<style scoped>
/* Vue Transition for FAQ expand/collapse */
.faq-expand-enter-active,
.faq-expand-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
  opacity: 1;
  overflow: hidden;
}

.faq-expand-enter,
.faq-expand-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.answer-content {
  padding-top: 0.5rem;
}

.answer-content p {
  margin: 0;
  line-height: 1.8;
}
</style>
